import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cx } from './cx';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
  className?: string;
}

/** Páginas visíveis: primeira, última e vizinhas da atual, com reticências entre os saltos. */
function pageItems(current: number, total: number): Array<number | 'gap'> {
  const pages = new Set([1, total, current - 1, current, current + 1].filter((p) => p >= 1 && p <= total));
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const items: Array<number | 'gap'> = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) items.push('gap');
    items.push(p);
  });
  return items;
}

const navBtn =
  'inline-flex items-center justify-center gap-1 min-h-[44px] md:min-h-0 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';

/** Paginação padrão das listas do admin. Mobile: Anterior / "x de y" / Próxima; desktop: números. */
const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onChange, className }) => {
  if (totalPages <= 1) return null;
  const go = (p: number) => onChange(Math.min(totalPages, Math.max(1, p)));

  return (
    <nav
      aria-label="Paginação"
      className={cx('flex items-center justify-between gap-3 bg-white px-4 py-3 border-t border-gray-200 sm:px-6', className)}
    >
      <button type="button" className={navBtn} onClick={() => go(currentPage - 1)} disabled={currentPage === 1}>
        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
        Anterior
      </button>

      <p className="text-sm text-gray-700 md:hidden">
        <span className="font-medium">{currentPage}</span> de <span className="font-medium">{totalPages}</span>
      </p>
      <div className="hidden md:flex items-center gap-1">
        {pageItems(currentPage, totalPages).map((item, i) =>
          item === 'gap' ? (
            <span key={`gap-${i}`} className="px-2 text-gray-400">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => go(item)}
              aria-current={item === currentPage ? 'page' : undefined}
              className={cx(
                'min-w-[2.25rem] px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                item === currentPage ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              {item}
            </button>
          )
        )}
      </div>

      <button type="button" className={navBtn} onClick={() => go(currentPage + 1)} disabled={currentPage === totalPages}>
        Próxima
        <ChevronRight className="w-4 h-4" aria-hidden="true" />
      </button>
    </nav>
  );
};

export default Pagination;
